import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit2, Trash2, Loader2, BookOpen, X, ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";
import { RichTextEditor } from "@/components/ui/RichTextEditor";

// ── Types ─────────────────────────────────────────────────────────────────────

type JenisPnbp = { id: string; kode: string; nama: string; urutan: number };

type PnbpCluster = {
  id: string;
  jenis_pnbp_id: string;
  title: string;
  content_html: string | null;
  urutan: number;
  is_active: boolean;
};

type ClusterFormData = {
  title: string;
  content_html: string;
  urutan: number;
};

// ── Modal ─────────────────────────────────────────────────────────────────────

function ClusterModal({
  jenis,
  cluster,
  onClose,
  onSaved,
}: {
  jenis: JenisPnbp;
  cluster: PnbpCluster | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!cluster;
  const [form, setForm] = useState<ClusterFormData>({
    title: cluster?.title ?? "",
    content_html: cluster?.content_html ?? "",
    urutan: cluster?.urutan ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError("Judul cluster tidak boleh kosong.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await api.put(`/master/pnbp-cluster/${cluster!.id}`, form);
      } else {
        await api.post("/master/pnbp-cluster", {
          jenis_pnbp_id: jenis.id,
          ...form,
        });
      }
      onSaved();
      onClose();
    } catch {
      setError("Gagal menyimpan. Silakan coba lagi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl mt-10 mb-10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              {isEdit ? "Edit Cluster" : "Tambah Cluster"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{jenis.kode} — {jenis.nama}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Judul */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Judul Cluster <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="mis. Landasan & Tujuan"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Urutan */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Urutan</label>
            <input
              type="number"
              value={form.urutan}
              onChange={(e) => setForm((f) => ({ ...f, urutan: Number(e.target.value) }))}
              className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Quill content */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Konten</label>
            <RichTextEditor
              value={form.content_html}
              onChange={(v) => setForm((f) => ({ ...f, content_html: v }))}
              placeholder="Tulis konten cluster di sini..."
              className="[&_.ql-editor]:min-h-[180px] [&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:rounded-t-lg border border-slate-300 rounded-lg overflow-hidden"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: "#1B5189" }}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Simpan Perubahan" : "Tambah Cluster"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PnbpClusterPage() {
  const qc = useQueryClient();
  const [selectedJenisId, setSelectedJenisId] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; cluster: PnbpCluster | null }>({
    open: false,
    cluster: null,
  });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Fetch jenis PNBP list
  const { data: jenisList = [], isLoading: jenisLoading } = useQuery<JenisPnbp[]>({
    queryKey: ["master", "jenis-pnbp-legacy"],
    // PnbpCluster masih FK ke m_jenis_pnbp → pakai endpoint legacy (6 jenis asli).
    queryFn: () => api.get<JenisPnbp[]>("/master/jenis-pnbp-legacy"),
    staleTime: 10 * 60 * 1000,
  });

  const selectedJenis = jenisList.find((j) => j.id === selectedJenisId) ?? null;

  // Fetch clusters for selected jenis
  const { data: clusters = [], isLoading: clustersLoading } = useQuery<PnbpCluster[]>({
    queryKey: ["master", "pnbp-cluster", selectedJenisId],
    queryFn: () =>
      api.get<PnbpCluster[]>("/master/pnbp-cluster", { jenis_pnbp_id: selectedJenisId }),
    enabled: !!selectedJenisId,
    staleTime: 2 * 60 * 1000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["master", "pnbp-cluster", selectedJenisId] });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/master/pnbp-cluster/${id}`),
    onSuccess: () => {
      invalidate();
      setDeletingId(null);
    },
  });

  return (
    <div className="flex gap-6 h-full">
      {/* ── Left: Jenis PNBP list ─────────────────────────────── */}
      <aside className="w-56 flex-shrink-0">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
          Jenis PNBP
        </p>
        {jenisLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Memuat...
          </div>
        ) : (
          <ul className="space-y-0.5">
            {jenisList.map((j) => (
              <li key={j.id}>
                <button
                  type="button"
                  onClick={() => setSelectedJenisId(j.id)}
                  className={clsx(
                    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                    selectedJenisId === j.id
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-600 hover:bg-slate-100",
                  )}
                >
                  <span className="text-xs font-bold opacity-50 flex-shrink-0 w-6">{j.kode}</span>
                  <span className="truncate">{j.nama}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* ── Right: Cluster list ───────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {!selectedJenis ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <BookOpen className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Pilih jenis PNBP di kiri untuk melihat cluster-nya.</p>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedJenis.nama}</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {clusters.length} cluster{clusters.length !== 1 ? "" : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModal({ open: true, cluster: null })}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: "#1B5189" }}
              >
                <Plus className="h-4 w-4" />
                Tambah Cluster
              </button>
            </div>

            {/* Cluster list */}
            {clustersLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Memuat cluster...
              </div>
            ) : clusters.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-slate-200 rounded-xl text-slate-400">
                <BookOpen className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">Belum ada cluster. Klik "Tambah Cluster" untuk memulai.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {clusters.map((c, idx) => (
                  <div
                    key={c.id}
                    className={clsx(
                      "rounded-xl border bg-white shadow-sm overflow-hidden",
                      c.is_active ? "border-slate-200" : "border-slate-100 opacity-60",
                    )}
                  >
                    {/* Row */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <span className="h-7 w-7 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.title}</p>
                        {!c.is_active && (
                          <span className="text-xs text-slate-400">nonaktif</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                        className="p-1 text-slate-400 hover:text-slate-700 transition-colors"
                      >
                        <ChevronDown
                          className={clsx(
                            "h-4 w-4 transition-transform",
                            expandedId === c.id && "rotate-180",
                          )}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ open: true, cluster: c })}
                        className="rounded-lg p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingId(c.id)}
                        className="rounded-lg p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Expanded content preview */}
                    {expandedId === c.id && (
                      <div className="border-t border-slate-100 px-4 py-3">
                        {c.content_html ? (
                          <div
                            className="prose prose-sm max-w-none text-slate-700"
                            dangerouslySetInnerHTML={{ __html: c.content_html }}
                          />
                        ) : (
                          <p className="text-xs text-slate-400 italic">Belum ada konten.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modal ──────────────────────────────────────────────── */}
      {modal.open && selectedJenis && (
        <ClusterModal
          jenis={selectedJenis}
          cluster={modal.cluster}
          onClose={() => setModal({ open: false, cluster: null })}
          onSaved={invalidate}
        />
      )}

      {/* ── Delete confirm ─────────────────────────────────────── */}
      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-bold text-slate-900 mb-2">Hapus Cluster?</h3>
            <p className="text-sm text-slate-600 mb-5">
              Cluster ini akan dihapus permanen dan tidak dapat dipulihkan.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeletingId(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deletingId)}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deleteMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
